import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import * as micropython from './micropython';
import * as tios from './tios';
import * as simulator from './simulator';

export default class DateTimeFormatNode extends BaseNode {
    constructor() {
        super();
        this.name = 'DateTime/Time Formatter';
        this.id = 'node_datetime_format';
        this.icon = 'GrDocumentTime';
        this.requirements = [['tios', 'micropython']];
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Execute',
                portId: 'time_format',
                params: [
                    {
                        name: 'time_variable',
                        dataType: TibboDataType.DATETIME,
                        description: 'The date/time variable to format.',
                        default: '',
                    },
                ],
            },
        ];

        this.outputs = [
            {
                name: 'Time String',
                portId: 'time_str',
                params: [
                    {
                        name: 'time_str',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'FORMAT',
                name: 'Format',
                settingType: SettingType.STRING,
                required: true,
                helpText: 'Possible values are: YYYY-MM-DD HH:mm:ss and YYYY-MM-DD hh:mm:ss tt, separators can be customized',
                default: 'YYYY-MM-DD hh:mm:ss tt',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies() {
        return ['datetime'];
    }

    getSettingValue(settingId: string): string {
        const project: ProjectConfiguration = this.project;
        if (settingId === 'DISPLAY_NAME') {
            const value = super.getSettingValue(settingId);
            if (value !== '') {
                return value;
            }
            const FORMAT = this.getSettingValue('FORMAT');
            return `Format Date/Time to ${FORMAT}`;
        }
        return super.getSettingValue(settingId);
    }

    getName(): string {
        const FORMAT = this.getSettingValue('FORMAT');
        return `Format Date/Time to ${FORMAT}`;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return `
##### Convert a Date/Time Variable to a String with Specified Format
This block gets the current date and time, which are output as a string using the selected formatting.`;
    }
}
